import { z } from 'zod';
import { ListChecks } from 'lucide-react';
import { OUTBOUND_CALL_OUTCOME_LABELS } from '@vagusx/shared';
import { Skeleton } from '@/components/ui/skeleton';
import { useDashboardMetric } from '@/hooks/use-dashboard-metric';
import type { TileDefinition } from './types';

/**
 * `outbound_outcomes` — the full disposition breakdown. One row per
 * disposition that occurred (zero-count dispositions are hidden to keep the
 * list tight), sorted most-frequent first, each with a count, share, and a
 * proportion bar. This is where the finer dispositions (spoke-no-action,
 * declined, callback, disconnected, wrong number, failed, transferred)
 * surface — the headline stat tiles only cover the top few.
 */
const LABELS: Record<string, string> = Object.fromEntries(
  OUTBOUND_CALL_OUTCOME_LABELS.map((o) => [o.value, o.label])
);

export const outboundOutcomesTile: TileDefinition = {
  type: 'outbound_outcomes',
  title: 'Call outcomes',
  description: 'Full disposition breakdown of outbound calls.',
  icon: ListChecks,
  metrics: ['outbound_outcomes'],
  defaultSize: { w: 6, h: 4 },
  minSize: { w: 4, h: 3 },
  configSchema: z.object({}).strict(),
  defaultConfig: {},
  render: ({ period, enabled }) => {
    const { metric, isLoading, isError } = useDashboardMetric({
      metricKey: 'outbound_outcomes',
      from: period.from,
      to: period.to,
      enabled,
    });

    if (isLoading) {
      return (
        <div className="flex h-full flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-6" />
          ))}
        </div>
      );
    }
    if (isError) {
      return (
        <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
          Failed to load
        </div>
      );
    }

    const total = metric?.total ?? 0;
    const rows = (metric?.items ?? []).filter((i) => i.count > 0).sort((a, b) => b.count - a.count);

    if (rows.length === 0) {
      return (
        <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
          No calls in this period
        </div>
      );
    }

    const max = rows[0]?.count ?? 1;

    return (
      <div className="flex h-full min-h-0 flex-col gap-1.5 overflow-y-auto pr-1">
        {rows.map((row) => {
          const share = total === 0 ? 0 : (row.count / total) * 100;
          const barPct = max === 0 ? 0 : (row.count / max) * 100;
          return (
            <div key={row.outcome} className="flex items-center gap-2 text-sm">
              <span className="w-40 shrink-0 truncate text-muted-foreground">
                {LABELS[row.outcome] ?? row.outcome}
              </span>
              <div className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-muted">
                <div className="h-full rounded-full bg-primary" style={{ width: `${barPct}%` }} />
              </div>
              <span className="w-10 shrink-0 text-right font-medium tabular-nums">
                {row.count.toLocaleString()}
              </span>
              <span className="w-10 shrink-0 text-right text-xs text-muted-foreground tabular-nums">
                {Math.round(share)}%
              </span>
            </div>
          );
        })}
      </div>
    );
  },
};
